import React from 'react';
import PropTypes from 'prop-types';
import theme from 'styled-theming';
import { Flex, Text, Box } from 'rebass';
import styled from 'styled-components';
import { FiCornerDownRight } from 'react-icons/fi';
import EditorField from '../Editor/editor-field';
import EditorDelete from '../Editor/EditorDelete';
import { checkValueType } from '../Editor/util';

// It should render a dropdown if there is a fallbackSkill present
// It should render a list of skills
// Clicking the option should set the fallbackSkill in the action payload

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.white,
});

const Title = styled(Text)`
  color: ${textColor};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const TitleContainer = styled(Flex)`
  color: ${textColor};
`;

const Icon = styled(FiCornerDownRight)`
  flex-shrink: 0;
  stroke-width: 1.25;
`;

function PolicyActionFallbackSkill(props) {
  const {
    rowItem,
    rowIndex,
    isEditing,
    temporaryPolicy,
    skills,
    validateObject,
    customAttributes,
    isLast,
  } = props;

  const valueType = checkValueType(rowItem.payload.skillId) === 'attribute' ? 'attribute' : 'skill';
  const isAttribute = valueType === 'attribute';
  const isErrored = rowItem?.errorArray?.includes('skillId');

  const selectItem = (item) => {
    const newObject = { ...temporaryPolicy };
    const { payload } = newObject.actions[rowIndex];
    newObject.actions[rowIndex].payload = { ...payload, skillId: isAttribute ? `{{${item}}}` : item.id };
    validateObject(newObject);
  };

  const removeFallbackSkill = () => {
    const newObject = { ...temporaryPolicy };
    const { userId } = newObject.actions[rowIndex].payload;
    if (isAttribute) {
      newObject.actions[rowIndex].payload = { userId, skillId: '{{}}' };
    } else {
      newObject.actions[rowIndex].payload = { userId };
    }
    validateObject(newObject);
  };

  const getPayloadDropdownValue = () => {
    if (isAttribute && skills.length) {
      const skillName = rowItem.payload.skillId.substr(2).slice(0, -2);
      const foundItem = customAttributes.find((attribute) => attribute === skillName);
      if (foundItem) {
        return foundItem;
      }
      return skillName;
    }
    if (!isAttribute && skills.length) {
      const foundItem = skills.find((skill) => `${skill.id}` === `${rowItem.payload.skillId}`);
      if (foundItem) {
        return foundItem;
      }
    }
    return '';
  };

  const hasFallback = () => {
    const skill = rowItem.payload.skillId;
    if (skill === undefined) {
      return false;
    }
    if (skill === null || typeof skill === 'string') {
      return true;
    }
    return false;
  };

  const selectValueType = (type) => {
    const newPolicy = { ...temporaryPolicy };
    switch (type) {
      case 'attribute':
        newPolicy.actions[rowIndex].payload.skillId = '{{}}';
        break;
      default:
        newPolicy.actions[rowIndex].payload.skillId = '-1';
        break;
    }
    validateObject(newPolicy);
  };

  const payloadValue = getPayloadDropdownValue();

  const getItems = (type) => {
    switch (type) {
      case 'skill':
        return skills;
      default:
        return customAttributes;
    }
  };

  const getPlaceholder = () => {
    let placeholder = '';
    placeholder = isAttribute ? 'Choose an attribute' : 'Choose a skill (optional)';
    return placeholder;
  };

  return (
    <Box
      data-testid="editor-fallback-skill"
      alignItems="center"
      px={2}
      pb={isLast ? 2 : 0}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gridGap: 2,
      }}
    >
      <TitleContainer pl={3}>
        <Icon size={16} />
        <Title flex={1} pl={2} fontSize={1} data-testid="fallback-row-label">fallback skill</Title>
      </TitleContainer>
      <Box
        sx={{
          display: 'grid',
          gridGap: 2,
          gridTemplateColumns: '1fr 2fr',
          alignItems: 'center',
        }}
      >
        {isEditing ? (
          <EditorField
            isEditing
            value={valueType}
            selectItem={(type) => selectValueType(type)}
            items={['skill', 'attribute']}
            noFreeText
          />
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gridGap: 2,
            gridTemplateColumns: hasFallback() ? '1fr auto' : 'auto',
            alignItems: 'center',
          }}
        >
          <EditorField
            isEditing={isEditing}
            actionType={`fallback-${valueType}`}
            value={payloadValue}
            selectItem={selectItem}
            items={getItems(valueType)}
            noFreeText={!isAttribute}
            placeholder={getPlaceholder()}
            isErrored={isErrored}
          />
          {hasFallback() && isEditing && (
            <EditorDelete ml={2} rowIndex={rowIndex} deleteItem={removeFallbackSkill} icon="delete" />
          )}
        </Box>
      </Box>
    </Box>
  );
}

PolicyActionFallbackSkill.propTypes = {
  isLast: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  temporaryPolicy: PropTypes.object.isRequired,
  skills: PropTypes.array.isRequired,
  customAttributes: PropTypes.array.isRequired,
  rowItem: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  validateObject: PropTypes.func.isRequired,
};

export default PolicyActionFallbackSkill;
